import React from 'react';
import { Loader2 } from 'lucide-react';

/**
 * <Button> — UI-1 Phase 1 button primitive.
 *
 * Variants: primary | secondary | tertiary | destructive
 * Sizes:    sm (32px) | md (40px) | lg (48px)
 * Slots:    icon (Lucide), trailingIcon, loading, disabled
 *
 * Behavior is style-only — onClick / disabled / data-testid pass through unchanged.
 * Wrapping a legacy <button> in <Button> is a 1:1 visual swap; PR1/PR2 keyframe
 * flows continue to work because handlers + testids are preserved.
 */

const VARIANT = {
  primary:
    'bg-[color:var(--color-primary)] text-white hover:bg-[color:var(--color-primary-hover)] active:bg-[color:var(--color-primary-active)] disabled:bg-[color:var(--color-primary)]/40',
  secondary:
    'bg-transparent text-[color:var(--color-primary)] border border-[color:var(--color-primary)]/40 hover:bg-[color:var(--color-primary-soft)] hover:border-[color:var(--color-primary)] active:bg-[color:var(--color-primary-soft)]',
  tertiary:
    'bg-transparent text-[color:var(--color-text-secondary)] hover:text-[color:var(--color-text-primary)] hover:bg-white/[0.05]',
  destructive:
    'bg-[color:var(--color-danger)] text-white hover:bg-[color:var(--color-danger-hover)] active:bg-[color:var(--color-danger-hover)]',
};

const SIZE = {
  sm: 'h-8 px-3 text-[12px] gap-1.5 rounded-[var(--radius-pill)]',
  md: 'h-10 px-4 text-[14px] gap-2 rounded-[var(--radius-pill)]',
  lg: 'h-12 px-5 text-[14px] gap-2 rounded-[var(--radius-pill)]',
};

const ICON_SIZE = { sm: 14, md: 16, lg: 18 };

export const Button = React.forwardRef(function Button(
  {
    children,
    variant = 'primary',
    size = 'md',
    icon: Icon,
    trailingIcon: TrailingIcon,
    loading = false,
    disabled = false,
    className = '',
    type = 'button',
    ...rest
  },
  ref,
) {
  const isDisabled = disabled || loading;
  const px = ICON_SIZE[size] || 16;
  return (
    <button
      ref={ref}
      type={type}
      disabled={isDisabled}
      className={`inline-flex items-center justify-center font-semibold whitespace-nowrap transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--color-surface)] disabled:cursor-not-allowed disabled:opacity-60 ${SIZE[size] || SIZE.md} ${VARIANT[variant] || VARIANT.primary} ${className}`}
      {...rest}
    >
      {loading ? (
        <Loader2 size={px} className="animate-spin shrink-0" />
      ) : Icon ? (
        <Icon size={px} className="shrink-0" />
      ) : null}
      {children && <span className="truncate">{children}</span>}
      {TrailingIcon && !loading ? <TrailingIcon size={px} className="shrink-0" /> : null}
    </button>
  );
});

export default Button;
